import React, { useCallback, useState } from "react";

import { Stack } from "@mui/material";
import { sortBy } from "lodash";

import { getPortfolioFamiliesById } from "@fartherfinance/frontend/api/Investment/request/getPortfolioFamiliesById";
import { getPortfolioPlanById } from "@fartherfinance/frontend/api/Investment/request/getPortfolioPlanById";
import { PortfolioFamilyId } from "@fartherfinance/frontend/api/Investment/types";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import { whitelistTaxStatus } from "../../Advisor/Investments/ModelMarketplaceV2/PortfolioPlanTab/PortfolioData";
import { countRepeatedLabelValues } from "../../PortfolioPlanV2/ModelPickerModal/dataHelpers";
import PortfolioFamilyOptions from "../../PortfolioPlanV2/ModelPickerModal/PortfolioFamilyOptions";
import ProviderDropdown from "../../PortfolioPlanV2/ModelPickerModal/ProviderDropdown";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import { DropdownMenuOption } from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import BaseDropdown from "@src/sharedComponents/Dropdown/BaseDropdown";
import TaxStatusLabel from "@src/sharedComponents/TaxStatusLabel/TaxStatusLabel";
import Text from "@src/sharedComponents/Typography/Text";
import { safeAwait } from "@src/utils/safeAwait";

import styles from "./SelectHypotheticalModel.module.css";

type DropDownState = {
  provider: DropdownMenuOption<PortfolioFamilyId> | undefined;
  modelSeries: DropdownMenuOption<PortfolioFamilyId> | undefined;
  assetAllocation: DropdownMenuOption<PortfolioFamilyId> | undefined;
};

type Props = {
  accountsInPortfolio: string;
  modelName: string;
  selectedTaxType?: DropdownMenuOption<PortfolioPlanId>;
  onSelectedTaxType: (
    portfolioPlanId: DropdownMenuOption<PortfolioPlanId> | undefined
  ) => void;
};

const PickHypothetical: React.FC<Props> = ({
  onSelectedTaxType,
  selectedTaxType,
  accountsInPortfolio,
  modelName,
}) => {
  const [dropdownState, setDropdownState] = useState<DropDownState>({
    provider: undefined,
    modelSeries: undefined,
    assetAllocation: undefined,
  });

  const clientAuth = useRequestAuth();
  const advisorAuth = useAdvisorRequestAuth();

  const auth = clientAuth ?? advisorAuth;

  const notify = useStatusNotification();

  const [taxStatusOption, setTaxStatusOption] = useState<
    DropdownMenuOption<PortfolioPlanId>[]
  >([]);

  const getTaxStatusOptions = useCallback(
    async ({ id }: { id: PortfolioFamilyId }) => {
      if (!auth) {
        throw new Error("Auth is not defined");
      }

      const resp = await safeAwait(getPortfolioFamiliesById(id, auth));
      if (resp.success !== true) {
        notify(`Failed to fetch Portfolio Family id#: ${id}`, "Error");
        return;
      }

      const portfolioPromises = resp.value.data.children
        .filter((portfolio) => portfolio.type === "Portfolio-Plan")
        .map((p) => {
          return getPortfolioPlanById(
            p.portfolioPlan.portfolioPlanId,
            {},
            auth
          );
        });

      const portfolioData = await safeAwait(Promise.all(portfolioPromises));

      if (portfolioData.success !== true) {
        notify("Failed to fetch Portfolio Plan data", "Error");
        return;
      }

      const taxStatus = portfolioData.value.map((p) => {
        const taxStatus = whitelistTaxStatus(p.data.plan.tags);

        return {
          id: p.data.plan.portfolioPlanId,
          label: taxStatus,
        };
      });

      const repeatedCounted = countRepeatedLabelValues(taxStatus);

      const uniqueList = taxStatus.map((t) => ({
        id: t.id,
        label: repeatedCounted[t.label] > 1 ? `${t.label} (${t.id})` : t.label, // if the number of appearances is greater than 1, append the id to the label
      }));

      // sort this alphabetically by label
      setTaxStatusOption(sortBy(uniqueList, (o) => o.label));
    },
    [auth, notify]
  );

  return (
    <>
      <div className={styles.heading}>
        <div className={styles.header}>{accountsInPortfolio}</div>

        <div className={styles.headerEnd}>{`Current Model: ${modelName}`}</div>
      </div>

      <BorderBox style={{ padding: 48, marginBottom: 38 }}>
        <Text variant={"title2"}>Hypothetical Model</Text>
        {/* First Row */}
        <Stack direction={"row"} width={"100%"} gap={4}>
          <Stack width={"100%"}>
            <ProviderDropdown
              id={dropdownState.provider?.id}
              label={dropdownState.provider?.label}
              onSelect={(p) => {
                setDropdownState({
                  ...dropdownState,
                  provider: p,
                  modelSeries: undefined,
                  assetAllocation: undefined,
                });
                onSelectedTaxType(undefined);
                setTaxStatusOption([]);
              }}
              height={260}
            />
          </Stack>

          <Stack width={"100%"}>
            <PortfolioFamilyOptions
              title="Model Series"
              portfolioFamilyId={dropdownState.provider?.id || null}
              curItem={dropdownState.modelSeries?.label}
              onSelect={(modelSeries) => {
                setDropdownState({
                  ...dropdownState,
                  modelSeries: modelSeries,
                  assetAllocation: undefined,
                });
                onSelectedTaxType(undefined);
                setTaxStatusOption([]);
              }}
              height={260}
            />
          </Stack>
        </Stack>

        {/* Second Row */}
        <Stack direction={"row"} width={"100%"} gap={4}>
          <Stack width={"100%"}>
            <PortfolioFamilyOptions
              title={"Asset Allocation"}
              portfolioFamilyId={dropdownState.modelSeries?.id || null}
              curItem={dropdownState.assetAllocation?.label}
              onSelect={(assetAlloc) => {
                setDropdownState({
                  ...dropdownState,
                  assetAllocation: assetAlloc,
                });
                onSelectedTaxType(undefined);
                setTaxStatusOption([]);
                getTaxStatusOptions(assetAlloc);
              }}
              sorting={(element) => {
                const parsed = Number(element.label.split("/")[0]);
                if (isNaN(parsed)) {
                  return element.label;
                }
                return parsed;
              }}
              height={230}
            />
          </Stack>

          <Stack width={"100%"}>
            <BaseDropdown
              isDisabled={taxStatusOption.length === 0}
              title={<TaxStatusLabel />}
              curItemLabel={selectedTaxType?.label}
              curItemId={selectedTaxType?.id}
              options={taxStatusOption}
              onSelect={onSelectedTaxType}
            />
          </Stack>
        </Stack>
      </BorderBox>
    </>
  );
};

export default PickHypothetical;
